import psl_ui from '../../libs/psl_ui.js';

import data from '../../data.js';
import CONFIG from '../../CONFIG.js';

import './style.scss';
import template from './template.pst';

import state from '../../state.js';
import analytics from '../analytics/component.js';
import psl_actions from '../../libs/psl_actions.js';

import lottie from 'lottie-web';

const base_url = './assets/';

const url_root = `${CONFIG.backend_base_url}files/`;

const lottie_era_map = {
	0: ['LottieCar_20s_01.json', 'LottieCar_20s_02.json'],
	1: ['LottieCar_50s_01.json', 'LottieCar_50s_02.json'],
	2: ['LottieCar_80s_01.json', 'LottieCar_80s_02.json'],
	3: ['LottieCar_00s_01.json', 'LottieCar_00s_02.json']
};

const era_car_counts = {
	0: 2,
	1: 2,
	2: 2,
	3: 2
};

// const era_car_counts = {
// 	0: 2,
// 	1: 3,
// 	2: 4,
// 	3: 5
// };

const era_speeds = {
	0: 0.02,// 0: 0.0175,
	1: 0.02,// 1: 0.0225,
	2: 0.02,// 2: 0.0275,
	3: 0.02,// 3: 0.0325
};

const RECENT_EDGES_COUNT = 5;

const MAP_WIDTH = 1080;
const MAP_HEIGHT = 1920;

const road_graphs = {};
const lottie_animations = {};
const lottie_movement_states = {};
const car_stopped_states = {};

function _calculate_distance(node1, node2) {
	const dx = (node1.x - node2.x) * MAP_WIDTH;
	const dy = (node1.y - node2.y) * MAP_HEIGHT;
	return Math.sqrt(dx * dx + dy * dy);
}

function _get_node_by_id(nodes, node_id) {
	return nodes.find(n => n.id === node_id);
}

function _get_edge_by_id(edges, edge_id) {
	return edges.find(e => e.id === edge_id);
}

function _get_available_edges(node_id, node_connections, edges, previous_edge_id, recent_edges) {
	const connected_edge_ids = node_connections[node_id] || [];
	let available_edges = connected_edge_ids
		.map(edge_id => _get_edge_by_id(edges, edge_id))
		.filter(edge => edge && (edge.from === node_id || edge.to === node_id));
	
	if (available_edges.length === 0) return [];
	
	if (previous_edge_id && available_edges.length > 1) {
		const filtered = available_edges.filter(edge => edge.id !== previous_edge_id);
		if (filtered.length > 0) available_edges = filtered;
	}
	
	if (recent_edges && recent_edges.length > 0 && available_edges.length > 1) {
		const filtered = available_edges.filter(edge => !recent_edges.includes(edge.id));
		if (filtered.length > 0) available_edges = filtered;
	}
	
	return available_edges;
}

function _get_next_node(edge, current_node_id) {
	return edge.from === current_node_id ? edge.to : edge.from;
}

function _move_along_edge(container, from_node, to_node, speed, movement_state) {
	const start_time = performance.now();
	const dx_normalized = to_node.x - from_node.x;
	const dy_normalized = to_node.y - from_node.y;
	const dx_pixels = dx_normalized * MAP_WIDTH;
	const dy_pixels = dy_normalized * MAP_HEIGHT;
	const distance_pixels = Math.sqrt(dx_pixels * dx_pixels + dy_pixels * dy_pixels);
	const speed_pixels_per_second = speed * MAP_WIDTH;
	const total_duration = (distance_pixels / speed_pixels_per_second) * 1000;
	
	if (movement_state.animation_frame_id) {
		cancelAnimationFrame(movement_state.animation_frame_id);
	}
	
	const animate = (current_time) => {
		if (movement_state.stopped) {
			movement_state.animation_frame_id = null;
			return;
		}
		
		const elapsed = current_time - start_time;
		const progress = Math.min(elapsed / total_duration, 1);
		
		const current_x = from_node.x + dx_normalized * progress;
		const current_y = from_node.y + dy_normalized * progress;
		
		container.style.left = `${current_x * 100}%`;
		container.style.top = `${current_y * 100}%`;
		
		if (progress < 1 && !movement_state.stopped) {
			movement_state.animation_frame_id = requestAnimationFrame(animate);
		} else {
			movement_state.animation_frame_id = null;
		}
	};
	
	movement_state.animation_frame_id = requestAnimationFrame(animate);
	
	return total_duration;
}

async function _load_road_graph(era_index) {
	if (road_graphs[era_index]) return road_graphs[era_index];
	
	try {
		const response = await fetch(`${base_url}roads/${era_index}.json?t=${Date.now()}`);
		const graph = await response.json();
		road_graphs[era_index] = graph;
		return graph;
	} catch (error) {
		console.error(`Failed to load road graph for era ${era_index}:`, error);
		return null;
	}
}

function _find_nearest_node(graph, x_percent, y_percent) {
	if (!graph || !graph.nodes || graph.nodes.length === 0) return null;
	
	let nearest_node = graph.nodes[0];
	let min_distance = Infinity;
	
	const x = x_percent / 100;
	const y = y_percent / 100;
	
	graph.nodes.forEach(node => {
		const dx = (node.x - x) * MAP_WIDTH;
		const dy = (node.y - y) * MAP_HEIGHT;
		const distance = Math.sqrt(dx * dx + dy * dy);
		
		if (distance < min_distance) {
			min_distance = distance;
			nearest_node = node;
		}
	});
	
	return nearest_node;
}

function _start_pathfinding(container, era_index, car_index) {
	const state_key = `${era_index}_${car_index}`;
	
	if (car_stopped_states[state_key]) {
		return;
	}
	
	if (!lottie_movement_states[state_key]) {
		lottie_movement_states[state_key] = {
			current_node_id: null,
			current_edge_id: null,
			timeout_id: null,
			animation_frame_id: null,
			recent_edges: [],
			stopped: false
		};
	}
	
	const movement_state = lottie_movement_states[state_key];
	
	if (movement_state.stopped) {
		return;
	}
	
	(async () => {
		const graph = await _load_road_graph(era_index);
		if (!graph || !graph.nodes || !graph.edges || !graph.node_connections) return;
		
		if (movement_state.stopped) return;
		
		const speed = era_speeds[era_index] || 0.01;
		
		if (!movement_state.current_node_id) {
			const current_left = parseFloat(container.style.left);
			const current_top = parseFloat(container.style.top);
			
			if (!isNaN(current_left) && !isNaN(current_top) && (current_left !== 0 || current_top !== 0)) {
				const nearest_node = _find_nearest_node(graph, current_left, current_top);
				if (nearest_node) {
					movement_state.current_node_id = nearest_node.id;
					container.style.left = `${nearest_node.x * 100}%`;
					container.style.top = `${nearest_node.y * 100}%`;
				} else {
					const random_node = graph.nodes[Math.floor(Math.random() * graph.nodes.length)];
					movement_state.current_node_id = random_node.id;
					container.style.left = `${random_node.x * 100}%`;
					container.style.top = `${random_node.y * 100}%`;
				}
			} else {
				const random_node = graph.nodes[Math.floor(Math.random() * graph.nodes.length)];
				movement_state.current_node_id = random_node.id;
				container.style.left = `${random_node.x * 100}%`;
				container.style.top = `${random_node.y * 100}%`;
			}
		} else {
			const current_left = parseFloat(container.style.left);
			const current_top = parseFloat(container.style.top);
			
			if (!isNaN(current_left) && !isNaN(current_top)) {
				const nearest_node = _find_nearest_node(graph, current_left, current_top);
				if (nearest_node) {
					const current_node = _get_node_by_id(graph.nodes, movement_state.current_node_id);
					if (current_node) {
						const distance_to_current = _calculate_distance(
							{ x: current_left / 100, y: current_top / 100 },
							current_node
						);
						const distance_to_nearest = _calculate_distance(
							{ x: current_left / 100, y: current_top / 100 },
							nearest_node
						);
						
						if (distance_to_nearest < distance_to_current) {
							movement_state.current_node_id = nearest_node.id;
						}
					}
				}
			}
		}
		
		if (movement_state.stopped) return;
		
		const current_node = _get_node_by_id(graph.nodes, movement_state.current_node_id);
		if (!current_node) return;
		
		const available_edges = _get_available_edges(
			movement_state.current_node_id,
			graph.node_connections,
			graph.edges,
			movement_state.current_edge_id,
			movement_state.recent_edges
		);
		
		if (available_edges.length === 0) return;
		
		if (movement_state.stopped) return;
		
		const next_edge = available_edges[Math.floor(Math.random() * available_edges.length)];
		const next_node_id = _get_next_node(next_edge, movement_state.current_node_id);
		const next_node = _get_node_by_id(graph.nodes, next_node_id);
		
		if (!next_node) return;
		
		if (movement_state.stopped) return;
		
		const current_left = parseFloat(container.style.left);
		const current_top = parseFloat(container.style.top);
		const start_node = (!isNaN(current_left) && !isNaN(current_top)) 
			? { x: current_left / 100, y: current_top / 100 }
			: current_node;
		
		const distance_pixels = _calculate_distance(start_node, next_node);
		const speed_pixels_per_second = speed * MAP_WIDTH;
		const duration = Math.max(100, Math.floor((distance_pixels / speed_pixels_per_second) * 1000));
		
		_move_along_edge(container, start_node, next_node, speed, movement_state);
		
		if (movement_state.stopped) return;
		
		movement_state.current_node_id = next_node_id;
		movement_state.current_edge_id = next_edge.id;
		
		if (!movement_state.recent_edges) {
			movement_state.recent_edges = [];
		}
		
		movement_state.recent_edges.push(next_edge.id);
		if (movement_state.recent_edges.length > RECENT_EDGES_COUNT) {
			movement_state.recent_edges.shift();
		}
		
		if (movement_state.timeout_id) {
			clearTimeout(movement_state.timeout_id);
		}
		
		if (!movement_state.stopped) {
			movement_state.timeout_id = setTimeout(() => {
				if (!movement_state.stopped) {
					_start_pathfinding(container, era_index, car_index);
				}
			}, duration);
		}
	})();
}

function _generate_cars_for_era(era) {
	const car_count = era_car_counts[era.i] || 2;
	const cars = [];
	
	for (let i = 0; i < car_count; i++) {
		const style = (i % 2 === 0) ? '01' : '02';
		const car_content = era.car_content && era.car_content[style] ? era.car_content[style] : [];
		
		cars.push({
			era: era.i,
			style: style,
			index: i,
			content: car_content,
			desc: 'pin'
		});
	}
	
	return cars;
}

const all_pins = data.eras.flatMap((era) => (era.pins || []).map((pin) => ({ ...pin, era: era.i })));
const all_cars = data.eras.flatMap((era) => _generate_cars_for_era(era).map((car) => ({ ...car })));

psl_ui.templates.add('pins',template);

const {els, rpoints}=psl_ui.templates.render('pins',{pins:all_pins, cars:all_cars, url_root});

const _container = document.getElementById('container');

els.forEach(el=>_container.appendChild(el));

const _pins_container = els[0];
const _pin_elements = _pins_container ? Array.from(_pins_container.querySelectorAll('.pin')) : [];
const _car_elements = _pins_container ? Array.from(_pins_container.querySelectorAll('.car')) : [];

_pin_elements.forEach(pin_el=>{
	Array.from(pin_el.querySelectorAll('.pin_content a:not(.close)')).forEach(a=>a.setAttribute('target','_blank'));
});

_car_elements.forEach(car_el=>{
	Array.from(car_el.querySelectorAll('.pin_content a:not(.close)')).forEach(a=>a.setAttribute('target','_blank'));
});

function _get_pin_content_rect(pin_el) {
	const content_el = pin_el.querySelector('.pin_content');
	if (!content_el) return null;
	
	const pin_rect = pin_el.getBoundingClientRect();
	const content_rect = content_el.getBoundingClientRect();
	
	return {
		left: content_rect.left,
		top: content_rect.top,
		right: content_rect.right,
		bottom: content_rect.bottom,
		width: content_rect.width,
		height: content_rect.height
	};
}

function _check_overlap(rect1, rect2) {
	if (!rect1 || !rect2) return false;
	
	return !(
		rect1.right < rect2.left ||
		rect1.left > rect2.right ||
		rect1.bottom < rect2.top ||
		rect1.top > rect2.bottom
	);
}

function _calculate_dynamic_position(car_el) {
	const container_el = document.querySelector('#container');
	const container_rect = container_el ? container_el.getBoundingClientRect() : null;
	if (!container_rect) return 'top-right';
	
	const popup_el = car_el.querySelector('.pin_content');
	const popup_rect = popup_el ? popup_el.getBoundingClientRect() : null;
	if (!popup_rect) return 'top-right';
	
	const popup_width = popup_rect.width;
	const popup_height = popup_rect.height;
	
	const car_rect = car_el.getBoundingClientRect();
	
	const margin = 24;
	const offset = 46;
	
	const top_right_left = car_rect.left + offset;
	const top_right_right = top_right_left + popup_width;
	const top_right_bottom = car_rect.top;
	const top_right_top = top_right_bottom - popup_height;
	
	const top_left_right = car_rect.right - offset;
	const top_left_left = top_left_right - popup_width;
	const top_left_bottom = car_rect.top;
	const top_left_top = top_left_bottom - popup_height;
	
	const bottom_right_left = car_rect.left + offset;
	const bottom_right_right = bottom_right_left + popup_width;
	const bottom_right_top = car_rect.bottom + 24;
	const bottom_right_bottom = bottom_right_top + popup_height;
	
	const bottom_left_right = car_rect.right - offset;
	const bottom_left_left = bottom_left_right - popup_width;
	const bottom_left_top = car_rect.bottom + 24;
	const bottom_left_bottom = bottom_left_top + popup_height;
	
	const can_place_top_right = top_right_left >= container_rect.left + margin && top_right_right <= container_rect.right - margin && top_right_top >= container_rect.top + margin;
	const can_place_top_left = top_left_left >= container_rect.left + margin && top_left_right <= container_rect.right - margin && top_left_top >= container_rect.top + margin;
	const can_place_bottom_right = bottom_right_left >= container_rect.left + margin && bottom_right_right <= container_rect.right - margin && bottom_right_bottom <= container_rect.bottom - margin;
	const can_place_bottom_left = bottom_left_left >= container_rect.left + margin && bottom_left_right <= container_rect.right - margin && bottom_left_bottom <= container_rect.bottom - margin;
	
	let position = 'top-right';
	
	if (can_place_top_right) position = 'top-right';
	else if (can_place_top_left) position = 'top-left';
	else if (can_place_bottom_right) position = 'bottom-right';
	else if (can_place_bottom_left) position = 'bottom-left';
	
	return position;
}

function _update_pins(){
	const current_era = state.era;
	_pin_elements.forEach((pin_el) => {
		const is_current_era = pin_el.classList.contains(`era_${current_era}`);
		
		if(is_current_era){
			pin_el.classList.add('active');
		} else {
			pin_el.classList.remove('active');
			pin_el.classList.remove('shown');
		}
	});
	
	_car_elements.forEach((car_el) => {
		const is_current_era = car_el.classList.contains(`era_${current_era}`);
		
		if(is_current_era){
			car_el.classList.add('active');
		} else {
			car_el.classList.remove('active');
			car_el.classList.remove('shown');
			
			let era_index = parseInt(car_el.getAttribute('data-era'));
			if (isNaN(era_index)) {
				if (car_el.classList.contains('era_0')) era_index = 0;
				else if (car_el.classList.contains('era_1')) era_index = 1;
				else if (car_el.classList.contains('era_2')) era_index = 2;
				else if (car_el.classList.contains('era_3')) era_index = 3;
				else era_index = 0;
			}
			
			const car_index = parseInt(car_el.getAttribute('data-car-index') || '0');
			const state_key = `${era_index}_${car_index}`;
			
			const movement_state = lottie_movement_states[state_key];
			if (movement_state && movement_state.stopped) {
				movement_state.stopped = false;
				car_stopped_states[state_key] = false;
				_start_pathfinding(car_el, era_index, car_index);
			}
		}
	});
}

function _init_lottie_animations() {
	_car_elements.forEach((car_el) => {
		let era_index = parseInt(car_el.getAttribute('data-era'));
		if (isNaN(era_index)) {
			if (car_el.classList.contains('era_0')) era_index = 0;
			else if (car_el.classList.contains('era_1')) era_index = 1;
			else if (car_el.classList.contains('era_2')) era_index = 2;
			else if (car_el.classList.contains('era_3')) era_index = 3;
			else era_index = 0;
		}
		
		const car_index = parseInt(car_el.getAttribute('data-car-index') || '0');
		const style = car_el.classList.contains('car_style_01') ? '01' : '02';
		
		const era = data.eras[era_index];
		if (!era) return;
		
		const files = lottie_era_map[era_index];
		if (!files) return;
		
		const file_index = style === '01' ? 0 : 1;
		const file = files[file_index];
		
		if (!file) return;
		
		const lottie_container = car_el.querySelector('.car_lottie');
		if (!lottie_container) return;
		
		const anim = lottie.loadAnimation({
			container: lottie_container,
			renderer: 'svg',
			loop: true,
			autoplay: true,
			path: `${base_url}img/lottie/${file}`
		});
		
		if (!lottie_animations[era_index]) {
			lottie_animations[era_index] = [];
		}
		
		lottie_animations[era_index].push({anim, container: car_el, car_index});
		
		_start_pathfinding(car_el, era_index, car_index);
	});
}

psl_actions.add({
	'pins.click':(e)=>{
		const clicked_pin = e.target;
		if(!clicked_pin) return;
		if(!clicked_pin.classList.contains('pin')) return;
		
		const is_showing = clicked_pin.classList.contains('shown');
		
		if (is_showing) {
			clicked_pin.classList.remove('shown');
		} else {
			const clicked_rect = _get_pin_content_rect(clicked_pin);
			
			_pin_elements.forEach((pin_el) => {
				if (pin_el === clicked_pin || !pin_el.classList.contains('shown')) return;
				
				const other_rect = _get_pin_content_rect(pin_el);
				if (_check_overlap(clicked_rect, other_rect)) {
					pin_el.classList.remove('shown');
				}
			});
			
			_car_elements.forEach((car_el) => {
				if (!car_el.classList.contains('shown')) return;
				
				const other_rect = _get_pin_content_rect(car_el);
				if (_check_overlap(clicked_rect, other_rect)) {
					car_el.classList.remove('shown');
					
					let era_index = parseInt(car_el.getAttribute('data-era'));
					if (isNaN(era_index)) {
						if (car_el.classList.contains('era_0')) era_index = 0;
						else if (car_el.classList.contains('era_1')) era_index = 1;
						else if (car_el.classList.contains('era_2')) era_index = 2;
						else if (car_el.classList.contains('era_3')) era_index = 3;
						else era_index = 0;
					}
					
					const car_index = parseInt(car_el.getAttribute('data-car-index') || '0');
					const state_key = `${era_index}_${car_index}`;
					
					const movement_state = lottie_movement_states[state_key];
					if (movement_state && movement_state.stopped) {
						movement_state.stopped = false;
						car_stopped_states[state_key] = false;
						_start_pathfinding(car_el, era_index, car_index);
					}
				}
			});
			
			clicked_pin.classList.add('shown');
			analytics.track_pin_click(clicked_pin.getAttribute('data-uid'));
		}
	},
	'pins.close':(e)=>{
		const clicked_pin = e.target.closest('.pin, .car');
		if (!clicked_pin) return;
		
		clicked_pin.classList.remove('shown');
		
		if (clicked_pin.classList.contains('car')) {
			let era_index = parseInt(clicked_pin.getAttribute('data-era'));
			if (isNaN(era_index)) {
				if (clicked_pin.classList.contains('era_0')) era_index = 0;
				else if (clicked_pin.classList.contains('era_1')) era_index = 1;
				else if (clicked_pin.classList.contains('era_2')) era_index = 2;
				else if (clicked_pin.classList.contains('era_3')) era_index = 3;
				else era_index = 0;
			}
			
			const car_index = parseInt(clicked_pin.getAttribute('data-car-index') || '0');
			const state_key = `${era_index}_${car_index}`;
			
			const movement_state = lottie_movement_states[state_key];
			if (movement_state && movement_state.stopped) {
				movement_state.stopped = false;
				car_stopped_states[state_key] = false;
				_start_pathfinding(clicked_pin, era_index, car_index);
			}
		}
	},
	'cars.click':(e)=>{
		const clicked_car = e.target;
		if(!clicked_car.classList.contains('car')) return;
		if (!clicked_car) return;
		
		let era_index = parseInt(clicked_car.getAttribute('data-era'));
		if (isNaN(era_index)) {
			if (clicked_car.classList.contains('era_0')) era_index = 0;
			else if (clicked_car.classList.contains('era_1')) era_index = 1;
			else if (clicked_car.classList.contains('era_2')) era_index = 2;
			else if (clicked_car.classList.contains('era_3')) era_index = 3;
			else era_index = 0;
		}
		
		const car_index = parseInt(clicked_car.getAttribute('data-car-index') || '0');
		const state_key = `${era_index}_${car_index}`;
		
		const is_showing = clicked_car.classList.contains('shown');
		
		if (is_showing) {
			clicked_car.classList.remove('shown');
			
			const movement_state = lottie_movement_states[state_key];
			if (movement_state && movement_state.stopped) {
				movement_state.stopped = false;
				car_stopped_states[state_key] = false;
				_start_pathfinding(clicked_car, era_index, car_index);
			}
		} else {
			const movement_state = lottie_movement_states[state_key];
			if (movement_state && !movement_state.stopped) {
				movement_state.stopped = true;
				car_stopped_states[state_key] = true;
				
				if (movement_state.timeout_id) {
					clearTimeout(movement_state.timeout_id);
					movement_state.timeout_id = null;
				}
				
				if (movement_state.animation_frame_id) {
					cancelAnimationFrame(movement_state.animation_frame_id);
					movement_state.animation_frame_id = null;
				}
			}
			
			const position = _calculate_dynamic_position(clicked_car);
			clicked_car.className = clicked_car.className.replace(/\b(top-left|top-right|bottom-left|bottom-right)\b/g, '');
			clicked_car.classList.add(position);
			
			const clicked_rect = _get_pin_content_rect(clicked_car);
			
			_pin_elements.forEach((pin_el) => {
				if (!pin_el.classList.contains('shown')) return;
				
				const other_rect = _get_pin_content_rect(pin_el);
				if (_check_overlap(clicked_rect, other_rect)) {
					pin_el.classList.remove('shown');
				}
			});
			
			_car_elements.forEach((car_el) => {
				if (car_el === clicked_car || !car_el.classList.contains('shown')) return;
				
				const other_rect = _get_pin_content_rect(car_el);
				if (_check_overlap(clicked_rect, other_rect)) {
					car_el.classList.remove('shown');
					
					let other_era_index = parseInt(car_el.getAttribute('data-era'));
					if (isNaN(other_era_index)) {
						if (car_el.classList.contains('era_0')) other_era_index = 0;
						else if (car_el.classList.contains('era_1')) other_era_index = 1;
						else if (car_el.classList.contains('era_2')) other_era_index = 2;
						else if (car_el.classList.contains('era_3')) other_era_index = 3;
						else other_era_index = 0;
					}
					
					const other_car_index = parseInt(car_el.getAttribute('data-car-index') || '0');
					const other_state_key = `${other_era_index}_${other_car_index}`;
					
					const other_movement_state = lottie_movement_states[other_state_key];
					if (other_movement_state && other_movement_state.stopped) {
						other_movement_state.stopped = false;
						car_stopped_states[other_state_key] = false;
						_start_pathfinding(car_el, other_era_index, other_car_index);
					}
				}
			});
			
			clicked_car.classList.add('shown');
			analytics.track_car_click(era_index, clicked_car.classList.contains('car_style_01') ? '01' : '02');
		}
	},
	// 'pins.ignore':()=>{
	// 	psl_actions.break();
	// }
});

_update_pins();

state.events.on('era',_update_pins);

state.events.on('roads',()=>{
	_pins_container.classList.toggle('roads_hidden',!state.roads);
});

state.events.on('reinit',()=>{
	_pin_elements.forEach((pin_el) => {
		pin_el.classList.remove('shown');
	});
	
	_car_elements.forEach((car_el) => {
		car_el.classList.remove('shown');
		
		let era_index = parseInt(car_el.getAttribute('data-era'));
		if (isNaN(era_index)) {
			if (car_el.classList.contains('era_0')) era_index = 0;
			else if (car_el.classList.contains('era_1')) era_index = 1;
			else if (car_el.classList.contains('era_2')) era_index = 2;
			else if (car_el.classList.contains('era_3')) era_index = 3;
			else era_index = 0;
		}
		
		const car_index = parseInt(car_el.getAttribute('data-car-index') || '0');
		const state_key = `${era_index}_${car_index}`;
		
		const movement_state = lottie_movement_states[state_key];
		if (movement_state && movement_state.stopped) {
			movement_state.stopped = false;
			car_stopped_states[state_key] = false;
			_start_pathfinding(car_el, era_index, car_index);
		}
	});
});

_init_lottie_animations();

setTimeout(()=>{
	_pins_container.classList.remove('hidden');
},250);
